import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { paymentsApi, ApiError } from '@/lib/pillarApi';
import {
    CardFormData,
    detectCardBrandConfiguration,
    formatCardNumber,
    formatExpiryDate,
    resolveCardBrand,
    tokenizeCardWithDelegateToken,
    validateCardFormData,
} from '@/lib/card-payment-entry';
import type {
    CardType,
    CustomerPaymentMethodListItem,
    CustomerPaymentMethodListResponse,
    CustomerPortalDashboardResponse,
    UpdateCustomerPaymentMethodResponse,
} from '@pillar-portal/api-types';
import { getCustomerOrganizationUuidFromSearchParams } from '@/app/customer/customer-organization-search';

const AUTO_RETRY_DELAY_MS = 3000;

export function describeCustomerPaymentMethod(
    paymentMethod: CustomerPaymentMethodListItem
): Readonly<{
    title: string;
    subtitle: string;
}> {
    const details = paymentMethod.payment_method_token_details;
    switch (details.payment_rail) {
        case 'CREDIT_CARD':
        case 'DEBIT_CARD':
            return {
                title: `${details.card_brand} ${details.masked_pan}`,
                subtitle: details.cardholder_name,
            };
        case 'ACH':
            return {
                title:
                    details.bank_name !== undefined && details.bank_name.length > 0
                        ? `${details.bank_name} ${details.masked_account_number}`
                        : details.masked_account_number,
                subtitle: details.account_holder_name,
            };
    }
}

const emptyCardFormData: CardFormData = {
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
};

type RetryCooldown = Readonly<{
    requestKey: string;
    retryAfterTs: number;
}>;

export function CustomerPaymentMethodsPageContent() {
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
    const [selectedMerchantOrganizationUuid, setSelectedMerchantOrganizationUuid] = useState<
        string | undefined
    >(undefined);
    const [newPaymentMethodFormData, setNewPaymentMethodFormData] =
        useState<CardFormData>(emptyCardFormData);
    const [newPaymentMethodErrorMessage, setNewPaymentMethodErrorMessage] = useState<
        string | null
    >(null);
    const [resolvedCardType, setResolvedCardType] = useState<CardType | null>(null);
    const [resolvedCardTypeRequestKey, setResolvedCardTypeRequestKey] = useState<string | null>(
        null
    );
    const [bootstrapRetryCooldown, setBootstrapRetryCooldown] = useState<RetryCooldown | null>(
        null
    );
    const [binLookupRetryCooldown, setBinLookupRetryCooldown] = useState<RetryCooldown | null>(null);
    const [paymentMethodPendingDelete, setPaymentMethodPendingDelete] =
        useState<CustomerPaymentMethodListItem | null>(null);
    const customerOrganizationUuid: string | undefined = getCustomerOrganizationUuidFromSearchParams(
        searchParams
    );

    const paymentMethodsQuery = useQuery<CustomerPaymentMethodListResponse>({
        queryKey: ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
        queryFn: async () =>
            await paymentsApi.listCustomerPaymentMethods(
                customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }
            ),
        retry: false,
    });

    const dashboardQuery = useQuery<CustomerPortalDashboardResponse>({
        queryKey: ['customerPortalDashboard', 'paymentMethodsPage', customerOrganizationUuid ?? 'self'],
        queryFn: async () =>
            await paymentsApi.getCustomerPortalDashboard(
                customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }
            ),
        retry: false,
    });

    const merchantOptions = dashboardQuery.data?.merchant_options ?? [];
    const effectiveMerchantOrganizationUuid =
        selectedMerchantOrganizationUuid
        ?? dashboardQuery.data?.selected_merchant_organization_uuid
        ?? merchantOptions[0]?.merchant_organization_uuid;
    const bootstrapRequestKey: string | null = effectiveMerchantOrganizationUuid ?? null;

    const bootstrapPaymentMethodMutation = useMutation({
        mutationFn: async (merchantOrganizationUuid: string) =>
            await paymentsApi.getCustomerPaymentMethodBootstrap({
                ...(customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }),
                merchant_organization_uuid: merchantOrganizationUuid,
            }),
        onMutate: (merchantOrganizationUuid: string) => {
            setBootstrapRetryCooldown((current) =>
                current?.requestKey === merchantOrganizationUuid ? null : current
            );
        },
        onError: (error: unknown) => {
            if (bootstrapRequestKey !== null) {
                setBootstrapRetryCooldown({
                    requestKey: bootstrapRequestKey,
                    retryAfterTs: Date.now() + AUTO_RETRY_DELAY_MS,
                });
            }
            setNewPaymentMethodErrorMessage(
                error instanceof ApiError
                    ? error.message
                    : 'Unable to prepare payment-method entry.'
            );
        },
    });

    const updatePaymentMethodMutation = useMutation<
        UpdateCustomerPaymentMethodResponse,
        unknown,
        {
            paymentMethodTokenUuid: string;
            storedForFutureUse: boolean;
        },
        {
            previous:
                | CustomerPaymentMethodListResponse
                | undefined;
        }
    >({
        mutationFn: async ({ paymentMethodTokenUuid, storedForFutureUse }) =>
            await paymentsApi.updateCustomerPaymentMethod({
                ...(customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }),
                payment_method_token_uuid: paymentMethodTokenUuid,
                stored_for_future_use: storedForFutureUse,
            }),
        onMutate: async ({ paymentMethodTokenUuid, storedForFutureUse }) => {
            await queryClient.cancelQueries({
                queryKey: ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
            });
            const previous =
                queryClient.getQueryData<CustomerPaymentMethodListResponse>([
                    'customerPaymentMethods',
                    customerOrganizationUuid ?? 'self',
                ]);
            if (previous !== undefined) {
                queryClient.setQueryData<CustomerPaymentMethodListResponse>(
                    ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
                    {
                        payment_methods: storedForFutureUse
                            ? previous.payment_methods.map((paymentMethod) =>
                                  paymentMethod.payment_method_token_uuid ===
                                      paymentMethodTokenUuid
                                      ? {
                                            ...paymentMethod,
                                            stored_for_future_use: storedForFutureUse,
                                        }
                                      : paymentMethod
                              )
                            : previous.payment_methods.filter(
                                  (paymentMethod) =>
                                      paymentMethod.payment_method_token_uuid
                                      !== paymentMethodTokenUuid
                              ),
                    }
                );
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(
                    ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
                    context.previous
                );
            }
        },
        onSettled: async () => {
            setPaymentMethodPendingDelete(null);
            await queryClient.invalidateQueries({
                queryKey: ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
            });
        },
    });

    const createPaymentMethodMutation = useMutation({
        mutationFn: async () => {
            if (bootstrapPaymentMethodMutation.data === undefined) {
                throw new Error('Secure payment-method entry is not ready.');
            }
            if (resolvedCardType === null) {
                throw new Error('Card type is not ready yet. Please wait a moment and retry.');
            }

            const validationErrorMessage = validateCardFormData(newPaymentMethodFormData);
            if (validationErrorMessage !== null) {
                throw new Error(validationErrorMessage);
            }

            const tokenizationResponse = await tokenizeCardWithDelegateToken({
                cardData: newPaymentMethodFormData,
                delegateToken: bootstrapPaymentMethodMutation.data.delegate_token,
            });
            const normalizedPan = newPaymentMethodFormData.cardNumber.replace(/\D/g, '');

            return await paymentsApi.createCustomerPaymentMethod({
                ...(customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }),
                merchant_organization_uuid:
                    bootstrapPaymentMethodMutation.data.merchant_organization_uuid,
                gateway_response: tokenizationResponse,
                card_brand: tokenizationResponse.card_brand,
                card_type: resolvedCardType,
                masked_pan: `XXXX${normalizedPan.slice(-4)}`,
                cardholder_name: newPaymentMethodFormData.cardholderName.trim(),
            });
        },
        onMutate: () => {
            setNewPaymentMethodErrorMessage(null);
        },
        onSuccess: async () => {
            setIsAddPaymentMethodOpen(false);
            setNewPaymentMethodFormData(emptyCardFormData);
            setNewPaymentMethodErrorMessage(null);
            await queryClient.invalidateQueries({
                queryKey: ['customerPaymentMethods', customerOrganizationUuid ?? 'self'],
            });
        },
        onError: (error: unknown) => {
            setNewPaymentMethodErrorMessage(
                error instanceof ApiError
                    ? error.message
                    : error instanceof Error && error.message.trim().length > 0
                        ? error.message
                        : 'Unable to add payment method.'
            );
        },
    });

    const customerPaymentMethodBinLookupMutation = useMutation({
        mutationFn: async (params: {
            merchantOrganizationUuid: string;
            panPrefix: string;
        }) =>
            await paymentsApi.customerPaymentMethodBinLookup({
                ...(customerOrganizationUuid === undefined
                    ? {}
                    : { customer_organization_uuid: customerOrganizationUuid }),
                merchant_organization_uuid: params.merchantOrganizationUuid,
                pan_prefix: params.panPrefix,
            }),
        onMutate: (variables) => {
            const requestKey = `${variables.merchantOrganizationUuid}:${variables.panPrefix}`;
            setBinLookupRetryCooldown((current) =>
                current?.requestKey === requestKey ? null : current
            );
        },
        onSuccess: (response, variables) => {
            setResolvedCardTypeRequestKey(
                `${variables.merchantOrganizationUuid}:${variables.panPrefix}`
            );
            setBinLookupRetryCooldown(null);
            setResolvedCardType(response.card_type);
        },
        onError: (_error, variables) => {
            setBinLookupRetryCooldown({
                requestKey: `${variables.merchantOrganizationUuid}:${variables.panPrefix}`,
                retryAfterTs: Date.now() + AUTO_RETRY_DELAY_MS,
            });
            setResolvedCardType(null);
        },
    });

    useEffect(() => {
        if (bootstrapRetryCooldown === null) {
            return;
        }

        const delayMs = Math.max(bootstrapRetryCooldown.retryAfterTs - Date.now(), 0);
        const timeoutId = window.setTimeout(() => {
            setBootstrapRetryCooldown((current) =>
                current?.requestKey === bootstrapRetryCooldown.requestKey ? null : current
            );
        }, delayMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [bootstrapRetryCooldown]);

    useEffect(() => {
        if (binLookupRetryCooldown === null) {
            return;
        }

        const delayMs = Math.max(binLookupRetryCooldown.retryAfterTs - Date.now(), 0);
        const timeoutId = window.setTimeout(() => {
            setBinLookupRetryCooldown((current) =>
                current?.requestKey === binLookupRetryCooldown.requestKey ? null : current
            );
        }, delayMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [binLookupRetryCooldown]);

    useEffect(() => {
        if (!isAddPaymentMethodOpen || effectiveMerchantOrganizationUuid === undefined) {
            return;
        }
        if (
            bootstrapPaymentMethodMutation.data?.merchant_organization_uuid
            === effectiveMerchantOrganizationUuid
        ) {
            return;
        }
        if (bootstrapPaymentMethodMutation.isPending) {
            return;
        }
        if (
            bootstrapRetryCooldown?.requestKey === effectiveMerchantOrganizationUuid
            && bootstrapRetryCooldown.retryAfterTs > Date.now()
        ) {
            return;
        }

        bootstrapPaymentMethodMutation.mutate(effectiveMerchantOrganizationUuid);
    }, [
        bootstrapRetryCooldown,
        bootstrapPaymentMethodMutation,
        effectiveMerchantOrganizationUuid,
        isAddPaymentMethodOpen,
    ]);

    const normalizedNewPaymentMethodPan = useMemo(
        () => newPaymentMethodFormData.cardNumber.replace(/\D/g, ''),
        [newPaymentMethodFormData.cardNumber]
    );
    const newPaymentMethodPanPrefix = useMemo(() => {
        if (normalizedNewPaymentMethodPan.length < 6) {
            return null;
        }
        return normalizedNewPaymentMethodPan.slice(0, 6);
    }, [normalizedNewPaymentMethodPan]);

    useEffect(() => {
        if (
            !isAddPaymentMethodOpen
            || bootstrapPaymentMethodMutation.data === undefined
            || newPaymentMethodPanPrefix === null
        ) {
            setResolvedCardType(null);
            setResolvedCardTypeRequestKey(null);
            return;
        }

        const requestKey = `${bootstrapPaymentMethodMutation.data.merchant_organization_uuid}:${newPaymentMethodPanPrefix}`;
        if (resolvedCardTypeRequestKey === requestKey) {
            return;
        }
        if (customerPaymentMethodBinLookupMutation.isPending) {
            return;
        }
        if (
            binLookupRetryCooldown?.requestKey === requestKey
            && binLookupRetryCooldown.retryAfterTs > Date.now()
        ) {
            return;
        }

        setResolvedCardType(null);
        customerPaymentMethodBinLookupMutation.mutate({
            merchantOrganizationUuid:
                bootstrapPaymentMethodMutation.data.merchant_organization_uuid,
            panPrefix: newPaymentMethodPanPrefix,
        });
    }, [
        binLookupRetryCooldown,
        bootstrapPaymentMethodMutation.data,
        customerPaymentMethodBinLookupMutation,
        isAddPaymentMethodOpen,
        newPaymentMethodPanPrefix,
        resolvedCardTypeRequestKey,
    ]);

    const currentCardBrand = useMemo(
        () => resolveCardBrand(newPaymentMethodFormData.cardNumber),
        [newPaymentMethodFormData.cardNumber]
    );

    if (paymentMethodsQuery.isLoading) {
        return <div className="p-6 text-sm text-muted-foreground">Loading payment methods...</div>;
    }

    if (paymentMethodsQuery.error !== null) {
        const errorMessage: string =
            paymentMethodsQuery.error instanceof ApiError
                ? paymentMethodsQuery.error.message
                : 'Failed to load payment methods.';
        return (
            <div className="p-6">
                <Alert variant="destructive">
                    <AlertTitle>Unable to load payment methods</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
            </div>
        );
    }

    const paymentMethods: ReadonlyArray<CustomerPaymentMethodListItem> =
        paymentMethodsQuery.data?.payment_methods ?? [];

    return (
        <div className="space-y-6 p-6">
            <div className="flex items-center justify-between gap-4">
                <div className="space-y-1">
                    <h1 className="text-3xl font-semibold tracking-tight">Payment Methods</h1>
                    <p className="text-sm text-muted-foreground">
                        Manage the stored payment methods available to your customer organization.
                    </p>
                </div>
                <Button
                    type="button"
                    onClick={() => {
                        setIsAddPaymentMethodOpen((current) => !current);
                        setNewPaymentMethodErrorMessage(null);
                    }}
                >
                    {isAddPaymentMethodOpen ? 'Close' : 'Add Payment Method'}
                </Button>
            </div>

            {isAddPaymentMethodOpen ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Add Payment Method</CardTitle>
                        <CardDescription>
                            Store a reusable card for future customer payments.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {dashboardQuery.isLoading ? (
                            <div className="text-sm text-muted-foreground">
                                Loading merchant options...
                            </div>
                        ) : null}

                        {dashboardQuery.error !== null ? (
                            <Alert variant="destructive">
                                <AlertTitle>Unable to load merchants</AlertTitle>
                                <AlertDescription>
                                    {dashboardQuery.error instanceof ApiError
                                        ? dashboardQuery.error.message
                                        : 'Please retry.'}
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {merchantOptions.length > 0 ? (
                            <>
                                <label className="space-y-2 text-sm font-medium text-slate-700">
                                    Merchant
                                    <select
                                        aria-label="Merchant"
                                        value={effectiveMerchantOrganizationUuid ?? ''}
                                        onChange={(event) => {
                                            setSelectedMerchantOrganizationUuid(
                                                event.target.value || undefined
                                            );
                                            setNewPaymentMethodErrorMessage(null);
                                        }}
                                        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                                    >
                                        {merchantOptions.map((merchantOption) => (
                                            <option
                                                key={merchantOption.merchant_organization_uuid}
                                                value={merchantOption.merchant_organization_uuid}
                                            >
                                                {merchantOption.merchant_display_name}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                {bootstrapPaymentMethodMutation.isPending ? (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                                        Preparing secure payment-method entry.
                                    </div>
                                ) : null}

                                {bootstrapPaymentMethodMutation.data !== undefined ? (
                                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                                        Secure entry ready for
                                        {' '}
                                        {bootstrapPaymentMethodMutation.data.merchant_display_name}.
                                    </div>
                                ) : null}

                                <div className="grid gap-4 md:grid-cols-2">
                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                        Card Number
                                        <input
                                            type="text"
                                            value={newPaymentMethodFormData.cardNumber}
                                            onChange={(event) => {
                                                setNewPaymentMethodFormData((current) => ({
                                                    ...current,
                                                    cardNumber: formatCardNumber(
                                                        event.target.value
                                                    ),
                                                }));
                                            }}
                                            placeholder="0000 0000 0000 0000"
                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                        />
                                    </label>

                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                        Cardholder Name
                                        <input
                                            type="text"
                                            value={newPaymentMethodFormData.cardholderName}
                                            onChange={(event) => {
                                                setNewPaymentMethodFormData((current) => ({
                                                    ...current,
                                                    cardholderName: event.target.value,
                                                }));
                                            }}
                                            placeholder="Name on card"
                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                        />
                                    </label>

                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                        Expiration Date
                                        <input
                                            type="text"
                                            value={newPaymentMethodFormData.expiryDate}
                                            onChange={(event) => {
                                                setNewPaymentMethodFormData((current) => ({
                                                    ...current,
                                                    expiryDate: formatExpiryDate(
                                                        event.target.value
                                                    ),
                                                }));
                                            }}
                                            placeholder="MM/YY"
                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                        />
                                    </label>

                                    <label className="space-y-2 text-sm font-medium text-slate-700">
                                        Security Code
                                        <input
                                            type="text"
                                            value={newPaymentMethodFormData.cvv}
                                            onChange={(event) => {
                                                const maxLength =
                                                    detectCardBrandConfiguration(
                                                        newPaymentMethodFormData.cardNumber
                                                    )?.securityCodeLength ?? 4;
                                                setNewPaymentMethodFormData((current) => ({
                                                    ...current,
                                                    cvv: event.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, maxLength),
                                                }));
                                            }}
                                            placeholder="999"
                                            className="w-full rounded-md border border-slate-300 px-3 py-2 text-base text-slate-900"
                                        />
                                    </label>
                                </div>

                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                                    <div className="flex items-center justify-between gap-4">
                                        <span>Detected card brand</span>
                                        <span className="font-medium text-slate-900">
                                            {currentCardBrand}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : null}

                        {newPaymentMethodErrorMessage !== null ? (
                            <Alert variant="destructive">
                                <AlertTitle>Unable to add payment method</AlertTitle>
                                <AlertDescription>
                                    {newPaymentMethodErrorMessage}
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        {customerPaymentMethodBinLookupMutation.isError ? (
                            <Alert variant="destructive">
                                <AlertTitle>Unable to verify card type</AlertTitle>
                                <AlertDescription>
                                    Please confirm the card number and retry.
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        <div className="flex items-center gap-3">
                            <Button
                                type="button"
                                disabled={
                                    bootstrapPaymentMethodMutation.data === undefined
                                    || resolvedCardType === null
                                    || createPaymentMethodMutation.isPending
                                }
                                onClick={() => {
                                    createPaymentMethodMutation.mutate();
                                }}
                            >
                                {createPaymentMethodMutation.isPending
                                    ? 'Adding Payment Method...'
                                    : 'Add Payment Method'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIsAddPaymentMethodOpen(false);
                                    setNewPaymentMethodFormData(emptyCardFormData);
                                    setNewPaymentMethodErrorMessage(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : null}

            {updatePaymentMethodMutation.isError ? (
                <Alert variant="destructive">
                    <AlertTitle>Unable to update payment method</AlertTitle>
                    <AlertDescription>
                        {updatePaymentMethodMutation.error instanceof ApiError
                            ? updatePaymentMethodMutation.error.message
                            : 'Please try again.'}
                    </AlertDescription>
                </Alert>
            ) : null}

            {paymentMethods.length === 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No payment methods</CardTitle>
                        <CardDescription>
                            No stored payment methods are currently available to this customer
                            organization.
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <div className="space-y-4">
                    {paymentMethods.map((paymentMethod) => {
                        const description = describeCustomerPaymentMethod(paymentMethod);
                        const isUpdatingCurrentMethod =
                            updatePaymentMethodMutation.isPending &&
                            updatePaymentMethodMutation.variables?.paymentMethodTokenUuid ===
                                paymentMethod.payment_method_token_uuid;

                        return (
                            <Card key={paymentMethod.payment_method_token_uuid}>
                                <CardHeader>
                                    <CardTitle className="text-xl">{description.title}</CardTitle>
                                    <CardDescription>{description.subtitle}</CardDescription>
                                </CardHeader>
                                <CardContent className="flex items-center justify-between gap-6">
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        disabled={isUpdatingCurrentMethod}
                                        onClick={() => {
                                            setPaymentMethodPendingDelete(paymentMethod);
                                        }}
                                    >
                                        Delete
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <ConfirmationModal
                open={paymentMethodPendingDelete !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPaymentMethodPendingDelete(null);
                    }
                }}
                message={
                    paymentMethodPendingDelete === null
                        ? ''
                        : `Delete ${describeCustomerPaymentMethod(paymentMethodPendingDelete).title}?`
                }
                onConfirm={() => {
                    if (paymentMethodPendingDelete === null) {
                        return;
                    }
                    updatePaymentMethodMutation.mutate({
                        paymentMethodTokenUuid:
                            paymentMethodPendingDelete.payment_method_token_uuid,
                        storedForFutureUse: false,
                    });
                }}
                onCancel={() => {
                    setPaymentMethodPendingDelete(null);
                }}
                confirmLabel="Delete"
                isLoading={updatePaymentMethodMutation.isPending}
            />
        </div>
    );
}

export default function CustomerPaymentMethodsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomerPaymentMethodsPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
